import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { SectionHeading, RequestInfoButton, Eyebrow } from "@/components/common";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSEO } from "@/lib/useSEO";
import { FOUNDATION, WHATSAPP, waLink } from "@/config/site";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowDown, X, MessageCircle } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1524069290683-0457abfe42c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const aboutImg = "https://images.unsplash.com/photo-1616674202799-054e7c831378?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Sparkles;
  return <C className={className} />;
}

export default function Foundation() {
  const { openEnquiry } = useEnquiry();
  const [lightbox, setLightbox] = useState(null);
  useSEO({
    title: "Atmasamman Foundation | Social Impact & Community Development",
    description: "Atmasamman Foundation is the social-impact initiative of Atmasamman Group, focused on creating opportunities through education, skills, awareness and community participation.",
    path: "/foundation",
  });

  return (
    <Layout waMessage={WHATSAPP.messages.foundation}>
      {/* HERO */}
      <section className="grad-navy relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="circuit-grid absolute inset-0 opacity-70" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-orange-500/15 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="animate-fade-up">
            <Eyebrow dark>Atmasamman Foundation</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl">
              Empowering People.<br /><span className="grad-orange-text">Strengthening Communities.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Atmasamman Foundation is the social-impact initiative of Atmasamman Group, focused on creating opportunities through education, skills, awareness and community participation.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#focus-areas" data-testid="foundation-focus-cta" className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-orange-600 hover:-translate-y-0.5 md:text-base">
                Explore Our Focus Areas <ArrowDown className="h-4 w-4" />
              </a>
              <button onClick={() => openEnquiry({ interest: "Atmasamman Foundation" })} data-testid="foundation-connect-cta"
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:border-orange-500/50 md:text-base">
                Connect With Foundation
              </button>
            </div>
          </div>
          <div className="animate-fade-up relative" style={{ animationDelay: "0.15s" }}>
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
              <img src={heroImg} alt="Community empowerment and education initiatives" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <img src={aboutImg} alt="Skill development in the community" className="w-full rounded-3xl border border-slate-200 object-cover shadow-lg" loading="lazy" />
          <div>
            <SectionHeading eyebrow="About the Foundation" title="Social impact at the heart of the Group" />
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Atmasamman Foundation represents the social-impact and community-development initiative of Atmasamman Group. Its broad purpose is to help make education, skills, digital literacy, technology awareness, career awareness and community opportunities more accessible.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Education", "Skills", "Digital Literacy", "Technology Awareness", "Career Awareness", "Community Opportunities"].map((t) => (
                <span key={t} className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section id="focus-areas" className="scroll-mt-24 bg-[#F8FAFC] py-16 md:py-24" data-testid="focus-areas-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Focus Areas" title="Where we focus our efforts"
            subtitle="These are the areas we work toward — creating access, awareness and opportunity." />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FOUNDATION.focusAreas.map((f) => (
              <div key={f.title} data-testid={`focus-${f.title.split(" ")[0].toLowerCase()}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-lg">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500 transition-colors group-hover:bg-orange-500 group-hover:text-white">
                  <Icon name={f.icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH JOURNEY */}
      <section className="py-16 md:py-24" data-testid="approach-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Our Approach" title="A journey from awareness to empowerment" />
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-5">
            {FOUNDATION.approach.map((a, i) => (
              <div key={a.step} className="relative rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#0B132B] font-mono text-sm font-bold text-orange-400">{i + 1}</div>
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{a.step}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.desc}</p>
                {i < FOUNDATION.approach.length - 1 && (
                  <ArrowRight className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-orange-400 md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INITIATIVES */}
      <section className="bg-[#F8FAFC] py-16 md:py-24" data-testid="initiatives-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Initiatives" title="Our Initiatives & Community Activities" />
          {FOUNDATION.initiatives.length === 0 ? (
            <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="initiatives-empty">
              <p className="text-base text-slate-600">Our initiatives and community activities will be shared here.</p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {FOUNDATION.initiatives.map((it) => (
                <div key={it.title} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  {it.featuredImage && <img src={it.featuredImage} alt={it.title} className="h-48 w-full object-cover" loading="lazy" />}
                  <div className="p-6">
                    <span className="font-mono text-xs uppercase tracking-wider text-orange-500">{it.category}</span>
                    <h3 className="mt-2 font-display text-lg font-bold text-slate-900">{it.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{it.shortDescription}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 md:py-24" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Gallery" title="Moments from our work" />
          {FOUNDATION.gallery.length === 0 ? (
            <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-dashed border-slate-300 bg-[#F8FAFC] p-10 text-center" data-testid="gallery-empty">
              <p className="text-base text-slate-600">Photos from our events, workshops and community activities will be shared here.</p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
              {FOUNDATION.gallery.map((g, i) => (
                <button key={i} onClick={() => setLightbox(g)} data-testid={`gallery-img-${i}`}
                  className="group overflow-hidden rounded-xl border border-slate-200">
                  <img src={g.src || g} alt={g.alt || `Foundation activity ${i + 1}`} className="h-40 w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute right-5 top-5 text-white" onClick={() => setLightbox(null)}><X className="h-7 w-7" /></button>
          <img src={lightbox.src || lightbox} alt={lightbox.alt || "Foundation activity"} className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}

      {/* IMPACT (only when verified metrics exist) */}
      {FOUNDATION.impact.length > 0 && (
        <section className="grad-navy py-16 md:py-24" data-testid="impact-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading center dark eyebrow="Impact" title="Our impact so far" />
            <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-4">
              {FOUNDATION.impact.map((m) => (
                <div key={m.label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                  <p className="font-display text-4xl font-extrabold text-orange-400">{m.value}</p>
                  <p className="mt-2 text-sm font-medium text-white/70">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GET INVOLVED */}
      <section className="bg-[#F8FAFC] py-16 md:py-24" data-testid="get-involved-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Get Involved" title="Be part of the change" />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FOUNDATION.getInvolved.map((g) => (
              <button key={g.title} onClick={() => openEnquiry({ interest: g.interest })} data-testid={`involve-${g.title.split(" ")[0].toLowerCase()}`}
                className="group flex flex-col items-start rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-lg">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B132B] text-orange-400 transition-colors group-hover:bg-orange-500 group-hover:text-white">
                  <Icon name={g.icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{g.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{g.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-orange-600">Get in touch <ArrowRight className="h-4 w-4" /></span>
              </button>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <RequestInfoButton interest="Atmasamman Foundation" testId="foundation-bottom-request" />
            <a href={waLink(WHATSAPP.messages.foundation)} target="_blank" rel="noopener noreferrer" data-testid="foundation-whatsapp"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-[#25D366] px-6 py-3 text-sm font-semibold text-[#128C4A] transition-all hover:bg-[#25D366] hover:text-white md:text-base">
              <MessageCircle className="h-4 w-4" /> WhatsApp Foundation
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
